import { useState } from "react";
import axios from "axios";
import { Check, Loader2, ClipboardCheck, FileDown, StickyNote } from "lucide-react";
import { CHECKLIST_ITEMS } from "../../data/site";
import { downloadChecklistPdf, downloadNotionTemplate } from "../../utils/checklist";
import { trackEvent } from "../../analytics/ga4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailOptin = ({ source = "checklist", compact = false, quizResult = null }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/subscribe`, { email, source, quiz_result: quizResult });
      trackEvent("generate_lead", { source, quiz_result: quizResult || undefined });
      setState("done");
    } catch (err) {
      setState("idle");
      setError("Please enter a valid email address.");
    }
  };

  if (state === "done") {
    return (
      <div className="rounded-2xl border border-volt/30 bg-cardink p-8" data-testid="optin-success">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-volt/15">
            <ClipboardCheck className="h-5 w-5 text-volt" />
          </span>
          <div>
            <p className="font-display text-lg font-bold">Your checklist is ready</p>
            <p className="text-sm text-white/50">Sent to <span className="text-volt">{email}</span> — grab your copy:</p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={downloadChecklistPdf} data-testid="download-pdf-button" className="flex items-center gap-2 rounded-full bg-volt px-6 py-3 text-sm font-bold text-ink transition-colors hover:bg-volt-hover">
            <FileDown className="h-4 w-4" /> Download PDF
          </button>
          <button onClick={downloadNotionTemplate} data-testid="download-notion-button" className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white/80 transition-colors hover:border-volt/50 hover:text-white">
            <StickyNote className="h-4 w-4 text-volt" /> Notion Template
          </button>
        </div>
        <ul className="mt-7 grid gap-2.5 sm:grid-cols-2">
          {CHECKLIST_ITEMS.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-white/75" data-testid={`checklist-item-${i}`}>
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" strokeWidth={3} />
              {item}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs text-white/40">Next up in your inbox: our 4-part series on picking the right platform for your business.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={compact ? "" : "w-full max-w-xl"} data-testid="optin-form">
      <div className={`flex ${compact ? "flex-col gap-3" : "flex-col gap-3 sm:flex-row"}`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourbusiness.com"
          data-testid="optin-email-input"
          className="flex-1 rounded-full border border-white/10 bg-white/5 px-6 py-3.5 text-sm text-white placeholder:text-white/35 focus:border-volt/60 focus:outline-none"
        />
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="optin-submit-button"
          className="flex items-center justify-center gap-2 rounded-full bg-volt px-7 py-3.5 text-sm font-bold text-ink transition-colors hover:bg-volt-hover disabled:opacity-60"
        >
          {state === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
          Get Free Checklist
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-red-400" data-testid="optin-error">{error}</p>}
      <p className="mt-3 text-xs text-white/40">Free PDF + Notion template. No spam — unsubscribe anytime.</p>
    </form>
  );
};

export default EmailOptin;
